import type { ReactNode } from 'react';
import { BellRing, Bot, Hand, LoaderCircle, PhoneCall, Plus, Reply, Undo2, UserRoundCheck, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDuration, formatPhone, formatSince, formatTime, formatWindowLeft } from '@/lib/conversationFormat';
import { deriveHandoffView, type HandoffView } from '@/lib/handoffState';
import type { TakeoverState } from '@/services/conversationService';
import { useServerNow } from './useServerNow';

export type HandoffBusy = 'takeover' | 'release' | 'extend' | 'resolve' | null;

type Props = {
  takeover: TakeoverState | null | undefined;
  replyWindowUntil: string | null | undefined;
  canReply: boolean;
  /** The contact's WhatsApp number is blocked at Meta: no reply path, the bar asks to call. */
  channelBlocked?: boolean;
  /** Logged-in account ("Du" for my own takeover). */
  me: string | null;
  customerPhone: string;
  busy: HandoffBusy;
  onTakeover: () => void;
  onRelease: () => void;
  onExtend: () => void;
  onResolve: () => void;
  /** Put the cursor into the reply box. */
  onReplyNow: () => void;
};

const RING_RADIUS = 11;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Share of the deadline window still left, as a ring that empties clockwise. */
export function DeadlineRing({ value }: { value: number }) {
  const offset = RING_CIRCUMFERENCE * (1 - Math.min(1, Math.max(0, value)));
  return (
    <svg viewBox="0 0 28 28" className="h-7 w-7 flex-shrink-0 -rotate-90" aria-hidden="true" data-testid="deadline-ring">
      <circle cx="14" cy="14" r={RING_RADIUS} fill="none" strokeWidth="3" className="stroke-border" />
      <circle
        cx="14"
        cy="14"
        r={RING_RADIUS}
        fill="none"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={offset}
        className={cn(
          'motion-safe:transition-[stroke-dashoffset] motion-safe:duration-1000 motion-safe:ease-linear',
          value < 0.2 ? 'stroke-warning' : 'stroke-brand',
        )}
      />
    </svg>
  );
}

function BarButton({
  onClick,
  disabled,
  loading,
  primary = false,
  icon,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  primary?: boolean;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'active:scale-[0.98] disabled:cursor-wait disabled:opacity-60',
        primary
          ? 'bg-brand text-brand-foreground shadow-sm hover:bg-brand-hover'
          : 'border border-border bg-background text-foreground hover:border-foreground/25 hover:bg-accent',
      )}
    >
      {loading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

/** A number that ticks every second: shown, but not re-announced by screen readers. */
function Tick({ ms }: { ms: number }) {
  return (
    <b className="font-semibold tabular-nums" aria-hidden="true">
      {formatDuration(ms)}
    </b>
  );
}

function deadlineClock(now: number, remainingMs: number | null): string | null {
  if (remainingMs === null) return null;
  return formatTime(new Date(now + remainingMs).toISOString());
}

function ReasonLine({ view }: { view: Exclude<HandoffView, { kind: 'agent' }> }) {
  if (!view.reason && !view.summary) return null;
  return (
    <p
      className="basis-full pl-10 text-xs leading-snug text-muted-foreground line-clamp-1 sm:line-clamp-2"
      title={view.summary ?? undefined}
    >
      {view.reason && <span className="font-medium text-foreground/80">{view.reason}</span>}
      {view.reason && view.summary && <span aria-hidden="true"> — </span>}
      {view.summary}
    </p>
  );
}

/**
 * Handoff status above a WhatsApp chat (livecenter §4): who answers right now,
 * how long the customer has been waiting, and when the agent steps back in —
 * with the deadline ring and exactly the actions that fit the state.
 */
export function HandoffStatusBar({
  takeover,
  replyWindowUntil,
  canReply,
  channelBlocked = false,
  me,
  customerPhone,
  busy,
  onTakeover,
  onRelease,
  onExtend,
  onResolve,
  onReplyNow,
}: Props) {
  const state = takeover?.active ? takeover.state ?? 'staff' : null;
  const ticking = state === 'requested' || state === 'staff';
  const now = useServerNow(ticking ? 1000 : 30_000, state !== null);
  const view = deriveHandoffView({ takeover, now, me, replyWindowUntil, canReply, channelBlocked });
  const locked = busy !== null;

  let icon: ReactNode;
  let main: ReactNode;
  let secondary: ReactNode = null;
  let srExtra: string | null = null;
  let actions: ReactNode;
  let tone: 'neutral' | 'urgent' | 'active' | 'callback' | 'closed' = 'neutral';
  let ring: number | null = null;

  switch (view.kind) {
    case 'agent':
      icon = <Bot className="h-4 w-4 text-muted-foreground" aria-hidden="true" />;
      main = <span className="text-muted-foreground">Agent antwortet automatisch</span>;
      actions = (
        <BarButton
          primary
          onClick={onTakeover}
          disabled={locked}
          loading={busy === 'takeover'}
          icon={<Hand className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          Übernehmen
        </BarButton>
      );
      break;

    case 'requested':
      tone = 'urgent';
      ring = view.ring;
      icon = <Hand className="h-4 w-4 text-brand" aria-hidden="true" />;
      main = (
        <>
          <span className="font-semibold">Kunde wartet auf einen Mitarbeiter</span>
          {view.waitingMs !== null && (
            <>
              {' · '}
              <Tick ms={view.waitingMs} />
            </>
          )}
        </>
      );
      if (view.remainingMs !== null) {
        secondary =
          view.remainingMs > 0 ? (
            <>
              Agent springt in <Tick ms={view.remainingMs} /> ein
            </>
          ) : (
            'Agent springt gleich ein'
          );
        srExtra = `Agent springt um ${deadlineClock(now, view.remainingMs)} ein.`;
      }
      actions = (
        <BarButton
          primary
          onClick={onTakeover}
          disabled={locked}
          loading={busy === 'takeover'}
          icon={<Hand className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          Übernehmen
        </BarButton>
      );
      break;

    case 'staff': {
      tone = 'active';
      ring = view.ring;
      icon = <UserRoundCheck className="h-4 w-4 text-brand" aria-hidden="true" />;
      const who = view.byMe ? 'Du schreibst' : `${view.takenBy?.trim() || 'Ein Mitarbeiter'} schreibt`;
      const waiting = view.deadlineKind === 'customer_waiting' && view.customerWaitingMs !== null;
      main = (
        <>
          <span className="font-semibold">{who}</span>
          {!waiting && view.remainingMs !== null && (
            <>
              {' · '}
              {view.remainingMs > 0 ? (
                <>
                  Rückgabe in <Tick ms={view.remainingMs} />
                </>
              ) : (
                'Rückgabe gleich'
              )}
            </>
          )}
        </>
      );
      if (waiting) {
        secondary = (
          <>
            <span className="font-medium text-foreground">
              Kunde wartet auf Antwort · <Tick ms={view.customerWaitingMs as number} />
            </span>
            {view.remainingMs !== null && (
              <>
                {' — '}
                {view.remainingMs > 0 ? (
                  <>
                    Agent springt in <Tick ms={view.remainingMs} /> ein
                  </>
                ) : (
                  'Agent springt gleich ein'
                )}
              </>
            )}
          </>
        );
      } else if (view.remainingMs !== null) {
        secondary = 'bei Ruhe im Chat';
      }
      if (view.remainingMs !== null) {
        srExtra = waiting
          ? `Agent springt um ${deadlineClock(now, view.remainingMs)} ein.`
          : `Rückgabe an den Agenten um ${deadlineClock(now, view.remainingMs)}.`;
      }
      actions = (
        <>
          <BarButton
            onClick={onExtend}
            disabled={locked}
            loading={busy === 'extend'}
            icon={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
          >
            <span className="sr-only">+5 min</span>
            <span aria-hidden="true">5 min</span>
          </BarButton>
          <BarButton
            onClick={onRelease}
            disabled={locked}
            loading={busy === 'release'}
            icon={<Undo2 className="h-3.5 w-3.5" aria-hidden="true" />}
          >
            <span className="sr-only">An Agenten zurückgeben</span>
            <span aria-hidden="true" className="2xl:hidden">Zurückgeben</span>
            <span aria-hidden="true" className="hidden 2xl:inline">An Agenten zurückgeben</span>
          </BarButton>
        </>
      );
      break;
    }

    case 'callback': {
      const since = view.sinceMs !== null ? formatSince(view.sinceMs) : null;
      const done = (
        <BarButton
          onClick={onResolve}
          disabled={locked}
          loading={busy === 'resolve'}
          icon={<CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          Erledigt
        </BarButton>
      );
      if (view.windowOpen) {
        tone = 'callback';
        icon = <BellRing className="h-4 w-4 text-brand" aria-hidden="true" />;
        const left = view.windowLeftMs !== null ? formatWindowLeft(view.windowLeftMs) : null;
        main = (
          <>
            <span className="font-semibold">Wartet auf Rückmeldung{since ? ` ${since}` : ''}</span>
            {left && <> · Antwortfenster {left}</>}
          </>
        );
        actions = (
          <>
            <BarButton
              primary
              onClick={onReplyNow}
              disabled={locked}
              loading={false}
              icon={<Reply className="h-3.5 w-3.5" aria-hidden="true" />}
            >
              Jetzt antworten
            </BarButton>
            {done}
          </>
        );
      } else {
        tone = 'closed';
        const phone = formatPhone(customerPhone);
        icon = <PhoneCall className="h-4 w-4 text-warning-strong" aria-hidden="true" />;
        main = (
          <>
            <span className="font-semibold">
              {view.channelBlocked ? 'WhatsApp-Nummer nicht erreichbar – bitte anrufen' : 'Antwortfenster abgelaufen – bitte anrufen'}
            </span>
            {phone && (
              <>
                {': '}
                <a
                  href={`tel:${phone}`}
                  className="font-semibold text-foreground underline decoration-warning underline-offset-2 hover:decoration-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  {phone}
                </a>
              </>
            )}
          </>
        );
        if (since) secondary = `Rückmeldung gewünscht ${since}`;
        actions = done;
      }
      break;
    }
  }

  return (
    <div
      data-testid="handoff-bar"
      className={cn(
        'relative flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2.5 transition-colors sm:px-5',
        tone === 'neutral' && 'border-border bg-card',
        tone === 'urgent' && 'border-brand/30 bg-brand-soft',
        tone === 'active' && 'border-border bg-card',
        tone === 'callback' && 'border-brand/25 bg-brand-soft',
        tone === 'closed' && 'border-warning/30 bg-warning/10',
      )}
    >
      {(tone === 'urgent' || tone === 'callback') && (
        <span className="absolute inset-y-0 left-0 w-[3px] bg-brand" aria-hidden="true" />
      )}
      <div className="flex min-w-[13rem] flex-1 items-center gap-3">
        {ring !== null ? (
          <span className="relative flex h-7 w-7 flex-shrink-0 items-center justify-center">
            <DeadlineRing value={ring} />
          </span>
        ) : (
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-background/70">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1" role="status">
          <p className="text-sm leading-snug text-foreground">{main}</p>
          {secondary && <p className="text-xs leading-snug text-muted-foreground">{secondary}</p>}
          {srExtra && <span className="sr-only">{srExtra}</span>}
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>
      {view.kind !== 'agent' && <ReasonLine view={view} />}
    </div>
  );
}

export default HandoffStatusBar;
